/**
 * @file Omega 和 Raven埋点函数
 */
import Omega from 'Omega'
import raven from '@/utils/raven-shim'
import { URL, ENV } from './dom'
import { getStore } from '@/store/store-standard'
import { APPLY_CHANNEL } from '@/types'
import { isMgm, isWebMarketing } from '@/utils/dom'
import Channel from './channel-lib'

interface ErrorAttributes {
  message?: string
  pub_omg_error_level?: 0 | 1 | 2 | 3 // 错误登记
  code?: string | number
  [key: string]: unknown // 允许扩展属性
}

/** 错误上报：排除接口报错场景，仅监控JS报错
 * @param eventid string
 * @param attrs object: pub_omg_error_level 级别，message 错误信息,code 错误码
 * @param error 错误信息
 * @returns Boolean
 */
const errorReportByCustom = (
  eventid: string,
  attrs: ErrorAttributes,
  error?: Error
) => {
  // 如果error 存在，获取相关数据 message line stack
  const errorAttrs: ErrorAttributes = {}
  if (error instanceof Error) {
    errorAttrs.message = error.message
    // name
    errorAttrs.name = error.name
    // 修复：Error 对象没有 lineNumber 属性，删除这行
    errorAttrs.stack = error.stack
  }

  try {
    /**
    name: <string> 错误名称
    code：<string|number> 错误代码
    msg: <string> 错误信息
    attrs：<Object> 其他属性
    **/
    Omega?.trackError(
      eventid,
      attrs.code,
      `${attrs?.message || ''}${errorAttrs?.message || ''}`,
      {
        ...attrs,
        ...errorAttrs,
      }
    )
  } catch (e) {
    console.warn('errorReportByCustom', e)
  }
}

interface ATTRS_PARAMS {
  [key: string]: string | number | unknown
}

/**
 * 私有属性，用于风控
 */
export enum PAGE {
  // 落地页
  landing = 0,
  // 注册页
  register = 1,
  // 授信等待页
  result = 2,
}

/**
 * 私有属性，用于风控
 */
export enum POSITION {
  // 手机号
  phone = 0,
  // 下载按钮
  download_btn = 1,
  // 协议
  protocol = 2,
}
interface PRIVATE_ATTRS_FOR_RISK {
  page: PAGE
  position: POSITION | null
  timestamp: number
  sw: number | null
  starttime: number | null
  endtime: number | null
}
class CacheQueue {
  config = {
    max: 60,
  }
  constructor() {
    try {
      const cache = window.localStorage.getItem(
        this.cacheKey
      )
      if (cache) {
        this.items = JSON.parse(cache)
      }
    } catch (e) {
      console.error('[Risk report error]', e)
    }
  }
  private cacheKey = '$Freya_kyc_tracker_cache_queue'
  private items = []
  get size() {
    return this.items.length
  }
  isEmpty() {
    return this.size === 0
  }
  enqueue(item: never) {
    // 如果达到最大存储，移除最先进入队列的
    if (this.size > this.config.max) {
      this.items.shift()
    }
    this.items.push(item)
    try {
      window.localStorage.setItem(
        this.cacheKey,
        JSON.stringify(this.items)
      )
    } catch (e) {
      console.error('[Risk report error]', e)
    }
  }
  dequeue() {
    const item = this.items.pop()
    if (this.isEmpty()) {
      try {
        window.localStorage.removeItem(this.cacheKey)
      } catch (e) {
        console.error('[Risk report error]', e)
      }
    }
    return item
  }
  // 批量发送
  iterator(send: (attrs: unknown) => void) {
    while (!this.isEmpty()) {
      send(this.dequeue())
    }
  }
}
let mediaData = {}

try {
  mediaData = window.adv.getMediaData()
} catch (e) {
  console.log('[window.adv]', e)
}
class TrackerClass {
  constructor() {
    this.dryRun()
    // adv初始化失败会白屏
    try {
      window.adv.initAdv({
        pageType: 'h5ocr',
        cluster: window.__INIT_STATE__.cluster,
        channel: isMgm()
          ? 'mgm'
          : window.__INIT_STATE__.resource,
        bid: 'cashloan',
        omega: Omega,
        loadHeatMap: window.__INIT_STATE__.env === 'prod',
      })
    } catch (e) {
      console.log('[window.adv]', e)
    }
  }

  private attrs = <ATTRS_PARAMS>{
    /**
     * 公参三批
     */
    // outcast_uuid: getAdvUUID(),
    /**
     * 公参一批
     */
    auditStatus: '',
    city_id: '',
    loan_status: '',
    order_status: '',
    is_mgm: isMgm() ? '1' : '0',

    page_source: URL.get('page_source'),
    tui: URL.get('tui'),
    // 国家默认值，登录后覆盖。
    country: 'MX',
    country_code: 'MX',
    /**
     * 公参二批
     */
    uid: '',
    pub_uid: '',
    pub_page: '',
    pub_from_page: '',
    cfrnid: '',
    credit_status: '',
    login_mobile: '', // 端外能传吗？
    apply_channel: APPLY_CHANNEL,
    applyChannel: APPLY_CHANNEL,
    pub_apply_channel: APPLY_CHANNEL,
    activity_id: URL.get('activity_id'),
    // campaign_id: URL.get('campaign_id'),
    channel_id: URL.get('channel_id'),
    resource_id: URL.get('resource_id'),
    ext: URL.get('ext'),
    pub_biz_line: 'fin',
    pub_subbiz: 'cashloan',
    os: ENV.os,
    operating_system: ENV.os,
    version: ENV.version,
    app_version: ENV.version,
    from_source: URL.get('from_source'),
    ...this.computedDetail(),
    ...mediaData,
  }

  setGlobalData(data: Record<string, unknown>) {
    for (const key in data) {
      this.attrs[key] = data[key]
    }
  }

  trackError = errorReportByCustom

  // 区分所有埋点的来源渠道等细节
  private computedDetail() {
    try {
      // 注意参数顺序
      return {
        error_code: [
          // 是否使用iframe通讯触发的埋点
          Channel.isUseFrame ? 1 : 0,
          // 是否178渠道来源（要求跳过落地页，立即登录）
          isWebMarketing() ? 1 : 0,
        ].join(','),
      }
    } catch {
      //
    }
    return {}
  }

  /**
   * Omega埋点
   */
  trackEvent(key: string, attrs?: Record<string, unknown>) {
    this.attrs.pub_page = getStore('pubPage') || ''
    this.attrs.pub_from_page = getStore('pubFromPage') || ''

    let ext = {
      pub_fin_channel: '',
      pub_resource_id: '',
      pub_fin_activity: '',
    }
    try {
      ext = JSON.parse(URL.get('ext') as string)
    } catch (e) {
      console.log('[Params ext not found]', e)
    }
    this.attrs.pub_fin_channel = ext?.pub_fin_channel
    this.attrs.pub_resource_id = ext?.pub_resource_id
    this.attrs.pub_fin_activity = ext?.pub_fin_activity

    Omega.setConfig({
      attrs: this.attrs,
    })
    raven.setAttrs(this.attrs)
    console.groupCollapsed(`[${key}]`)
    console.log(attrs)
    console.log(this.attrs)
    console.groupEnd()
    Omega.trackEvent(key, attrs)
    raven.trackEvent(key, attrs)
  }
  /**
   * 风控私参
   */
  private privateAttrsForRisk = <PRIVATE_ATTRS_FOR_RISK>{
    page: PAGE.landing,
    position: null,
    timestamp: 0,
    sw: null,
    starttime: null,
    endtime: null,
  }

  /**
   * 用数组模拟队列，FIFO原则
   */
  private cacheQueue = new CacheQueue()

  /**
   * 风控埋点key
   * @todo 替换成新埋点
   */
  private riskKey = 'fin_loan_abroad_land_time_sw'

  /**
   * 该方法触发的埋点会在登录后批量派发
   * @note only for 风控
   */
  trackWithCache(attrs: Record<string, unknown>) {
    attrs = {
      ...this.privateAttrsForRisk,
      ...attrs,
    }
    attrs.timestamp = Date.now()
    // 如果未登录
    if (!getStore('user')) {
      return this.cacheQueue.enqueue(attrs as never)
    }
    // 已登录，则直接发送
    this.trackEvent(this.riskKey, attrs)
    // 已登录，队列不为空
    this.dryRun()
  }

  /**
   * 媒体事件回传
   */
  trackWithMedia(scene: any) {
    const { resource, env, switcher } =
      window.__INIT_STATE__
    if (!switcher.media) {
      return
    }
    const user = getStore('user')
    try {
      // 营销adv库加载失败，不报错
      window.adv
        .mediaEventUp(scene, {
          env,
          channel: resource,
          httpUp: true,
          pixelUp: false,
          data: {
            mobile: user?.cell,
            uid: user?.uid,
            callingCode: '52',
          },
        })
        .catch((e) => console.log('[Event media error]', e))
    } catch (e) {
      console.log('[Event media error]', e)
    }
  }

  /**
   * 人拉人埋点
   */
  trackWithMgm(
    key: string,
    attrs?: Record<string, unknown>
  ) {
    if (this.attrs['is_mgm'] !== '1') {
      return
    }
    this.trackEvent(key, attrs)
  }

  /**
   * 天眼埋点
   */
  trackWithRaven(
    key: string,
    attrs?: Record<string, unknown>
  ) {
    console.log('[Raven]', key, attrs)
    attrs = attrs || {}
    raven.trackEvent('tech_freya_kyc', {
      position: key,
      ...attrs,
    })
  }

  dryRun() {
    if (this.cacheQueue.isEmpty() || !getStore('user')) {
      return
    }
    this.cacheQueue.iterator((attrs) =>
      this.trackEvent(
        this.riskKey,
        attrs as Record<string, unknown>
      )
    )
  }
}

export const Tracker = new TrackerClass()

export const trackEvent = (
  key: string,
  attrs?: Record<string, unknown>
) => {
  Tracker.trackEvent(key, attrs)
}
